/**
 * Surface components (ui-ux §19.0–§19.5, §19.12, §19.17, §3.2): tier panels, painted frames, cards, header bands,
 * banderoles, roundels, dividers, drop caps, gift rows, the arcade tab bar, top bars and status strips.
 * Owner: UI/Visual (F3).
 */
import type { ReadonlySignal, TabId } from '../../shared';
import { h, type Child } from '../dom';

export type Tier = 'illum' | 'plate' | 'rubric' | 'plain';

export interface PanelOpts {
  tier?: Tier;
  title?: Child;
  /** Heading element for the title (h2 section / h3 card title). */
  titleAs?: 'h2' | 'h3';
  /** Lombard on the title's first letter (granted by the OrnamentBudget: ≤ 1 per viewport). */
  lombard?: boolean | 'blue';
  /** Corner flourishes (≤ 1 set per viewport). */
  flourish?: boolean;
  /** `alt` = blue penwork; `none` = no penwork edge. */
  penwork?: 'red' | 'alt' | 'none';
  /** Plate ground (Plate tier). */
  ground?: 'gold' | 'lapis' | 'vermilion';
  /** Painted frame for Illuminated / Plate tiers. */
  frame?: 'gilt' | 'plate-card' | 'plate-band';
  class?: string;
  attrs?: Record<string, string>;
}

/** A region of one surface tier (§19.0.3); counted ornaments carry `data-orn`. */
export function panel(o: PanelOpts, ...children: Child[]): HTMLElement {
  const tier = o.tier ?? 'rubric';
  const cls = [`mc-tier-${tier}`];
  const attrs: Record<string, string | boolean> = { ...(o.attrs ?? {}) };
  if (tier === 'rubric') {
    cls.push('mc-panel');
    if (o.penwork === 'alt') attrs['data-penwork'] = 'alt';
    if (o.penwork === 'none') attrs['data-penwork'] = 'none'; else attrs['data-orn'] = 'penwork';
    if (o.lombard) attrs['data-lombard'] = o.lombard === 'blue' ? 'blue' : 'red';
    if (o.flourish) attrs['data-flourish'] = true;
  }
  if (o.frame) { cls.push('mc-frame9'); attrs['data-frame'] = o.frame; attrs['data-orn'] = tier === 'illum' ? 'frame' : 'plate'; }
  if (o.ground) attrs['data-ground'] = o.ground;
  if (o.class) cls.push(o.class);
  const title = o.title !== undefined ? h(o.titleAs ?? 'h3', { class: o.titleAs === 'h2' ? 'mc-section' : 'mc-title' }, o.title) : null;
  return h('section', { class: cls.join(' '), attrs }, title, ...children, o.flourish ? h('span', { class: 'mc-flourish-br', attrs: { 'aria-hidden': 'true' } }) : null);
}

/** A content card (§19.5): leading media, a stretched title link or plain title, caption, optional CTA. */
export function card(o: { media?: Node; title: Child; href?: string; caption?: Child; cta?: Node; state?: 'locked' | 'ready'; plate?: boolean; extra?: Child }): HTMLElement {
  const titleEl = o.href ? h('a', { class: 'mc-card__link mc-title', attrs: { href: o.href } }, o.title) : h('span', { class: 'mc-title' }, o.title);
  const attrs: Record<string, string> = {};
  if (o.state) attrs['data-state'] = o.state;
  if (o.plate) { attrs['data-frame'] = 'plate-card'; attrs['data-orn'] = 'plate'; }
  return h('article', { class: `mc-card${o.plate ? ' mc-frame9 mc-tier-plate' : ''}`, attrs },
    o.media ?? h('span'), h('div', { class: 'mc-card__body' }, titleEl, o.caption ? h('p', { class: 'mc-caption', style: 'margin:0' }, o.caption) : null, o.extra ?? null),
    o.cta ? (o.cta instanceof HTMLElement ? (o.cta.classList.add('mc-card__cta'), o.cta) : o.cta) : null);
}

/** A section header band (vermilion tile, or lapis with stars). */
export function band(text: Child, o: { lapis?: boolean } = {}): HTMLDivElement {
  return h('div', { class: `mc-band${o.lapis ? ' mc-band--lapis' : ''}`, attrs: { role: 'heading', 'aria-level': '2' } }, h('span', { class: 'mc-band__text' }, text));
}

/** A banderole (scroll ribbon) title; the optional sub-line sits under the ribbon (the ribbon holds one line). */
export function banderole(text: Child, sub?: Child): HTMLDivElement {
  return h('div', { class: 'mc-banderole-wrap', attrs: { 'data-orn': 'plate' } },
    h('div', { class: 'mc-banderole' }, h('span', { class: 'mc-banderole__text' }, text)),
    sub ? h('span', { class: 'mc-banderole__sub' }, sub) : null);
}

/** A roundel portrait / medallion (32 / 44 / 48 / 64 / 96). */
export function roundel(content: Node, o: { size?: 32 | 44 | 48 | 64 | 96; side?: 'player' | 'foe'; tier?: string; label?: string } = {}): HTMLSpanElement {
  const attrs: Record<string, string> = {};
  if (o.side) attrs['data-side'] = o.side;
  if (o.label) { attrs.role = 'img'; attrs['aria-label'] = o.label; } else attrs['aria-hidden'] = 'true';
  return h('span', { class: `mc-roundel${o.size && o.size !== 64 ? ` mc-roundel--${o.size}` : ''}`, attrs, style: o.tier ? { '--_tier': o.tier } : undefined }, content);
}

/** The rubric divider (1 px vermilion, gap, 1 px ink, a lozenge). */
export function divider(): HTMLDivElement { return h('div', { class: 'mc-rule', attrs: { role: 'separator', 'data-orn': 'divider' } }); }

/** A paragraph with a CSS drop cap (≤ 2 per viewport; chronicle day/chapter starts, Итог, Распутье). */
export function dropCapParagraph(text: string, o: { alt?: boolean; read?: boolean } = {}): HTMLParagraphElement {
  return h('p', { class: `mc-dropcap${o.alt ? ' mc-dropcap--alt' : ''}${o.read !== false ? ' mc-read' : ''}`, attrs: { 'data-orn': 'dropcap' } }, text);
}

export interface GiftCell { gift: 'xp' | 'goods' | 'virtue' | 'honour'; on: boolean; icon: Node; value?: string }

/** A gift row (§19.17): always the four gifts in order; off = sketch at 30 %. */
export function giftRow(cells: GiftCell[], o: { label: string; large?: boolean }): HTMLUListElement {
  const order = ['xp', 'goods', 'virtue', 'honour'] as const;
  const byGift = new Map(cells.map((c) => [c.gift, c]));
  return h('ul', { class: `mc-gifts${o.large ? ' mc-gifts--lg' : ''}`, attrs: { 'aria-label': o.label } },
    order.map((g) => {
      const c = byGift.get(g);
      return h('li', { attrs: { 'data-gift': g, 'data-on': !!c?.on } }, c?.icon ?? null, c?.value ? h('span', null, c.value) : null);
    }));
}

export interface TabSpec { id: TabId; label: string; compactLabel?: string; href: string; icon: Node; badge?: number; locked?: string; ariaLabel?: string }

/** The arcade tab bar (§19.3): halves around the raised Ристалище seal; `inline` for showcases, `rail` for desk. */
export function tabBar(o: { tabs: TabSpec[]; current: ReadonlySignal<TabId | null> | TabId | null; label: string; inline?: boolean; rail?: boolean; onSelect?: (id: TabId) => void }): HTMLElement {
  const cur = (): TabId | null => (o.current && typeof o.current === 'object' ? o.current.get() : o.current as TabId | null);
  const cell = (t: TabSpec): HTMLAnchorElement => {
    const seal = t.id === 'arena';
    const attrs: Record<string, string | boolean> = { href: t.href, 'data-tab': t.id, 'aria-label': t.ariaLabel ?? t.label };
    if (t.locked) { attrs['data-locked'] = true; attrs.title = t.locked; }
    if (!seal) attrs['data-orn'] = 'arcade';
    const iconWrap = seal ? h('span', { class: 'mc-seal-disc', attrs: { 'data-orn': 'seal' } }, t.icon) : h('span', { class: 'mc-tab__icon' }, t.icon);
    const a = h('a', {
      class: `mc-tab${seal ? ' mc-tab--seal' : ''}`, attrs,
      on: { click: (e) => { if (o.onSelect) { e.preventDefault(); o.onSelect(t.id); } } },
    }, iconWrap, h('span', { class: 'mc-tab__label', attrs: { 'aria-hidden': 'true' } },
      h('span',{class:'mc-tab__label-full'},t.label), t.compactLabel?h('span',{class:'mc-tab__label-compact'},t.compactLabel):null), t.badge ? h('span', { class: 'mc-badge', attrs: { 'aria-hidden': 'true' } }, String(t.badge)) : null);
    return a;
  };
  const byId = new Map(o.tabs.map((t) => [t.id, t]));
  const pick = (ids: TabId[]): HTMLAnchorElement[] => ids.map((id) => byId.get(id)).filter((t): t is TabSpec => !!t).map(cell);
  const nav = h('nav', { class: `mc-tabbar${o.inline ? ' mc-tabbar--inline' : ''}${o.rail ? ' mc-tabbar--rail' : ''}`, attrs: { 'aria-label': o.label } },
    h('div', { class: 'mc-tabbar__half' }, pick(['life', 'road'])),
    ...pick(['arena']),
    h('div', { class: 'mc-tabbar__half' }, pick(['cards', 'order'])));
  const sync = (): void => {
    const c = cur();
    for (const a of nav.querySelectorAll<HTMLAnchorElement>('a.mc-tab')) {
      if (a.dataset.tab === c) a.setAttribute('aria-current', 'page'); else a.removeAttribute('aria-current');
    }
  };
  sync();
  if (o.current && typeof o.current === 'object') o.current.subscribe(sync);
  return nav;
}

/** A top bar (52 px + safe top): back / title / actions; `night` = the lapisNight chrome tier. */
export function topBar(o: { title?: Child; start?: Node | null; end?: Child; night?: boolean }): HTMLElement {
  return h('header', { class: `mc-topbar${o.night ? ' mc-topbar--night' : ''}` },
    o.start ?? h('span', { style: 'inline-size:44px' }),
    h('div', { class: 'mc-topbar__title' }, o.title ?? ''),
    h('div', { style: 'display:flex;gap:4px;align-items:center;min-inline-size:44px;justify-content:flex-end' }, o.end ?? null));
}

/** The Житие-style status strip on the lapisNight chrome: ≤ 4 chips + one action at the far right. */
export function statusStrip(chips: Node[], action?: Node): HTMLElement {
  return h('header', { class: 'mc-strip' }, ...chips, h('span', { class: 'mc-strip__spacer' }), action ?? null);
}

/** A quatrefoil medallion (omen keystone, loot reveal): ink rim, gold lobes, lapis core; `p` = closing ring 0..1. */
export function medallion(content: Node, o: { size?: number; p?: number; label?: string } = {}): HTMLSpanElement {
  const ns = 'http://www.w3.org/2000/svg';
  let ring: SVGSVGElement | null = null;
  if (o.p !== undefined) {
    ring = document.createElementNS(ns, 'svg');
    ring.setAttribute('class', 'mc-medallion__ring');
    ring.setAttribute('viewBox', '0 0 100 100');
    ring.innerHTML = '<circle cx="50" cy="50" r="47" pathLength="100"/>';
  }
  return h('span', {
    class: 'mc-medallion', attrs: o.label ? { role: 'img', 'aria-label': o.label, 'data-orn': 'seal' } : { 'aria-hidden': 'true', 'data-orn': 'seal' },
    style: { '--_s': `${o.size ?? 64}px`, ...(o.p !== undefined ? { '--p': String(o.p) } : {}) },
  }, ring, h('span', { class: 'mc-medallion__core' }, content));
}
